import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import Grid from 'react-bootstrap/lib/Grid';
import { Link } from 'react-router-dom';
import Thumbnail from 'components/Thumbnails/Thumbnail';
import { useDispatch, useSelector } from 'react-redux';

import Panel from 'components/Panel/Panel';
import SvgIcon from 'components/SvgIcon/SvgIcon';
import DisplaynameSection from './components/DisplaynameSection';
import HireButton from './components/Buttons/HireButton';
import ShortlistButton from './components/Buttons/ShortlistButton';
import BioSnippet from './components/BioSnippet';
import ResumeUpload from 'containers/Onboarding/ResumeUpload';
import Loader from 'components/Loader/Loader';
import { uploadResume } from 'redux/modules/auth';
import styles from './PortfolioHeader.module.scss';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from 'react-bootstrap';
import { loadUser } from 'redux/modules/portfolio';
import { trackEvent } from 'helpers/Analytics';
import { setSuccessMessage } from 'redux/modules/successMessage';

const {
  carouselButton,
  previous,
  chevron,
  displayname,
  avatar,
  next,
  bioSnippet,
  bioSnippetXS,
  bottomRow,
  buttonContainer,
  buttonContainerXS,
  coverContent,
  portfolioCover,
  topRow,
  xsPanel,
} = styles;

const PortfolioHeader = ({
  existingBriefs,
  loggedInUser,
  nextUser,
  prevUser,
  shortlistState,
  toggleShortlist,
  onAccept,
  onHire,
  onHireByDay,
  onRatings,
  onUpdateAvatar,
  onUpdateCover,
  pitches,
  user,
  isLoadingOwnBriefs,
}) => {
  const dispatch = useDispatch();

  const {
    uploadingResume,
    uploadedResume,
    uploadResumeError,
    uploadResumeErrorDetails,
    uploadResumeSuccess,
  } = useSelector((state) => state.auth);

  const [showResumeUploader, setShowResumeUploader] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [selectedResumeFile, setSelectedResumeFile] = useState(null);

  useEffect(() => {
    if (uploadedResume && uploadResumeError && !uploadingResume) {
      trackEvent('user-uploads-resume-portfolio-error', {
        error_message: uploadResumeError,
        error_details:
          uploadResumeErrorDetails || 'No detailed error information available',
      });
      setUploadError(uploadResumeError);
      setUploading(false);
    }
  }, [uploadResumeError, uploadedResume, uploadingResume]);

  useEffect(() => {
    if (uploadedResume && uploadResumeSuccess && !uploadingResume) {
      trackEvent('user-uploads-resume-portfolio-success');
      setUploadSuccess(true);
      setUploading(false);
      dispatch(
        setSuccessMessage('Resume processed and portfolio updated successfully')
      );
      dispatch(loadUser(user.username)).then(() => {
        setShowResumeUploader(false);
        setUploadError(null);
        setUploadSuccess(false);
        setSelectedResumeFile(null);
      });
    }
  }, [uploadedResume, uploadingResume]);

  const handleResumeFileSelect = (file) => {
    if (!file) return;
    setSelectedResumeFile(file);
    setUploadError(null);
  };

  const handleResumeUpload = () => {
    if (!selectedResumeFile) return;

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(false);

    trackEvent('user-uploads-resume-portfolio', {
      fileExtension: selectedResumeFile.name.split('.').pop().toLowerCase(),
    });
    dispatch(uploadResume(selectedResumeFile));
  };

  // Only render if user is loaded
  if (!user) {
    return null;
  }

  const desktopStyle = {
    filter: 'drop-shadow(1px 1px 4px rgba(0, 0, 0, 0.25))',
  };

  const currentUser = loggedInUser && user && loggedInUser.id === user.id;

  const landingBanner = (nextUser || prevUser) && (
    <div
      className="text-center"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'white',
        width: '100%',
        position: 'relative',
        height: '55px',
      }}
    >
      {prevUser && (
        <Link
          to={prevUser.links.main_relative}
          className={`${carouselButton} ${previous}`}
        >
          <SvgIcon
            className={chevron}
            icon="chevron-left"
            size="16"
            color="#fff"
          />
          <p className={displayname}>{prevUser.displayname}</p>
          <Thumbnail thumbnails={prevUser.avatars} className={avatar} circle />
        </Link>
      )}
      {nextUser && (
        <Link
          to={nextUser.links.main_relative}
          className={`${carouselButton} ${next}`}
        >
          <Thumbnail thumbnails={nextUser.avatars} circle className={avatar} />
          <p className={displayname}>{nextUser.displayname}</p>
          <SvgIcon
            className={chevron}
            icon="chevron-right"
            size="16"
            color="#fff"
          />
        </Link>
      )}
      <HireButton
        onAccept={onAccept}
        currentUser={currentUser}
        existingBriefs={existingBriefs}
        onHire={onHire}
        onHireByDay={onHireByDay}
        loggedInUser={loggedInUser}
        pitches={pitches}
        user={user}
        isLoadingOwnBriefs={isLoadingOwnBriefs}
      />
    </div>
  );

  const mobilePanel = (
    <Panel className={xsPanel}>
      <BioSnippet tagName="h3" className={bioSnippetXS} lines={4} user={user} />

      <div className={buttonContainerXS}>
        {loggedInUser && !currentUser && (
          <ShortlistButton
            mobile
            shortlistState={shortlistState}
            toggleShortlist={toggleShortlist}
            style={{ marginBottom: '1em' }}
          />
        )}
        <HireButton
          onAccept={onAccept}
          currentUser={currentUser}
          existingBriefs={existingBriefs}
          onHire={onHire}
          onHireByDay={onHireByDay}
          loggedInUser={loggedInUser}
          pitches={pitches}
          user={user}
          style={{ width: '100%' }}
          isLoadingOwnBriefs={isLoadingOwnBriefs}
        />
      </div>
    </Panel>
  );

  return (
    <div>
      <Grid
        fluid
        style={{ width: '100%', padding: 0 }}
        className={'portfolioGrid'}
      >
        {landingBanner}
        {/* <div className="lato-regular" style={{ display: 'flex' }}> */}
        <div
          id="portfolio-header"
          className={portfolioCover}
          style={{
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            position: 'relative',
            width: '100%',
            bottom: 0,
            left: 0,
            right: 0,
            top: 0,
          }}
        >
          {user && (
            <Thumbnail
              className={portfolioCover}
              thumbnails={user.covers}
              id="portfolio-cover"
              style={{
                height: '100%',
                position: 'absolute',
                cursor: currentUser ? 'pointer' : 'initial',
              }}
              onClick={currentUser && onUpdateCover}
              boxShadow="rgb(0 0 0 / 40%) 0px 0px 0px 2000px inset"
            />
          )}
          <div className={coverContent}>
            <div className={topRow}>
              {/* edit button */}
              {/* {currentUser && (
                  <Fade in={coverEditMode} mountOnEnter unmountOnExit>
                    <div
                      style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                      }}
                      className="pointer"
                    >
                      <Button
                        // onClick={onUpdateAvatar}
                        onClick={onUpdateCover}
                        className={styles.editButton}
                        children={<SvgIcon icon="pencil" color="#ffffff" />}
                      />
                    </div>
                  </Fade>
                )} */}
              <div>
                {user ? (
                  <Thumbnail
                    thumbnails={user.avatars}
                    className="portfolio-avatar left"
                    id="portfolio-avatar"
                    onClick={currentUser && onUpdateAvatar}
                    style={{
                      position: 'relative',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      cursor: currentUser ? 'pointer' : 'initial',
                    }}
                    circle
                  />
                ) : (
                  <div
                    className="portfolio-avatar left"
                    onClick={onUpdateAvatar}
                    style={{ borderRadius: '20%', overflow: 'hidden' }}
                  />
                )}
                {/* edit button */}
                {/* {currentUser && (
                    <Fade in={avatarEditMode} mountOnEnter unmountOnExit>
                      <div
                        className="portfolio-avatar--edit left pointer"
                        onClick={onUpdateAvatar}
                      >
                        <SvgIcon icon="pencil" color="#ffffff" />
                      </div>
                    </Fade>
                  )} */}
              </div>
              <BioSnippet
                tagName="h1"
                lines={3}
                className={`text-white ${bioSnippet}`}
                user={user}
              />
            </div>
            <div className={bottomRow}>
              <DisplaynameSection
                isOwner={currentUser ? true : false}
                onRatings={onRatings}
                user={user}
              />

              <div className={buttonContainer}>
                {currentUser && (
                  <Button
                    onClick={() => setShowResumeUploader(true)}
                    bsSize="lg"
                    bsStyle="primary"
                    className={styles.uploadButton}
                  >
                    <SvgIcon icon="upload" size="24" color="#fff" />{' '}
                    <strong>Upload Resume</strong>
                  </Button>
                )}
                {loggedInUser && !currentUser && (
                  <ShortlistButton
                    shortlistState={shortlistState}
                    toggleShortlist={toggleShortlist}
                    style={desktopStyle}
                  />
                )}
                {!currentUser && (
                  <HireButton
                    onAccept={onAccept}
                    currentUser={currentUser}
                    existingBriefs={existingBriefs}
                    onHire={onHire}
                    onHireByDay={onHireByDay}
                    loggedInUser={loggedInUser}
                    pitches={pitches}
                    user={user}
                    style={desktopStyle}
                    isLoadingOwnBriefs={isLoadingOwnBriefs}
                  />
                )}
              </div>
              {currentUser && (
                <Button
                  onClick={() => setShowResumeUploader(true)}
                  bsSize="lg"
                  bsStyle="primary"
                  block
                  className={[styles.uploadButton, styles.uploadButtonMobile]}
                >
                  <SvgIcon icon="upload" size="24" color="#fff" />{' '}
                  <strong>Upload Resume</strong>
                </Button>
              )}
            </div>
          </div>
        </div>
      </Grid>
      {mobilePanel}
      <Modal
        show={showResumeUploader}
        onHide={() => {
          if (!uploading) {
            setShowResumeUploader(false);
            setUploadError(null);
            setUploadSuccess(false);
            setSelectedResumeFile(null);
          }
        }}
      >
        <ModalHeader closeButton={!uploading}>
          <ModalTitle className="lato-bold">Upload Resume</ModalTitle>
        </ModalHeader>
        <ModalBody bsClass={styles.resumeModal}>
          {uploading && (
            <div className={styles.loadingOverlay}>
              <Loader loading />
              <span className={styles.loadingOverlayText}>
                Uploading your resume, hang tight...
              </span>
            </div>
          )}
          <div className={styles.resumeWarning}>
            <SvgIcon
              className={styles.resumeWarningIcon}
              icon="warning"
              size="32"
            />
            <div>
              <p className={styles.resumeWarningText}>
                <strong>Warning:</strong>{' '}
                {`Uploading will overwrite your bio with content from your resume. You can't recover your previous bio, but can edit your bio at any time.`}
              </p>
            </div>
          </div>
          <ResumeUpload
            onUpload={handleResumeFileSelect}
            uploading={uploading}
            uploadError={uploadError}
            uploadSuccess={uploadSuccess}
            showReupload={false}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            onClick={() => {
              if (!uploading) {
                setShowResumeUploader(false);
                setUploadError(null);
                setUploadSuccess(false);
                setSelectedResumeFile(null);
              }
            }}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            bsStyle="primary"
            onClick={handleResumeUpload}
            disabled={!selectedResumeFile || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

PortfolioHeader.propTypes = {
  editMode: PropTypes.bool,
  editDisplayname: PropTypes.string,
  existingBriefs: PropTypes.array,
  loggedInUser: PropTypes.object,
  nextUser: PropTypes.oneOf([PropTypes.object, PropTypes.bool]),
  prevUser: PropTypes.oneOf([PropTypes.object, PropTypes.bool]),
  shortlistState: PropTypes.bool,
  toggleShortlist: PropTypes.func,
  verificationState: PropTypes.bool,
  onAccept: PropTypes.func,
  onHire: PropTypes.func,
  onVerify: PropTypes.func,
  onHireByDay: PropTypes.func,
  onRatings: PropTypes.func,
  onUpdateDisplayname: PropTypes.func,
  uploadResume: PropTypes.func,
  onUpdateRoles: PropTypes.func,
  onUpdateAvatar: PropTypes.func,
  onUpdateCover: PropTypes.func,
  pitches: PropTypes.oneOf([PropTypes.array, PropTypes.bool]),
  user: PropTypes.object,
  isLoadingOwnBriefs: PropTypes.bool,
};

export default PortfolioHeader;
